import React, { useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiGrid, FiList, FiSearch, FiPlus, FiChevronDown, FiX, FiCheckCircle } = FiIcons;

const templates = [
  { id: 'implant', title: 'Dental Implant', icon: '🦷', description: 'Single tooth implant procedure' },
  { id: 'full_arch', title: 'Full Arch Implant Therapy', icon: '🔧', description: 'Complete arch restoration' },
  { id: 'extraction', title: 'Surgical Extraction', icon: '⚕️', description: 'Complex tooth extraction' },
  { id: 'wisdom_teeth', title: 'Wisdom Teeth', icon: '🦷', description: 'Wisdom teeth removal' },
  { id: 'jaw_surgery', title: 'Jaw Surgery', icon: '🏥', description: 'Orthognathic or corrective jaw surgery' },
  { id: 'bone_graft', title: 'Bone Grafting', icon: '🔬', description: 'Bone augmentation procedure' },
  { id: 'biopsy', title: 'Pathology/Biopsy', icon: '🔍', description: 'Tissue examination and diagnosis' },
  { id: 'tmj', title: 'TMJ Consultation', icon: '💭', description: 'Temporomandibular joint evaluation' },
  { id: 'trauma', title: 'Trauma', icon: '🚑', description: 'Emergency dental trauma care' },
  { id: 'nerve', title: 'Nerve', icon: '⚡', description: 'Nerve-related dental procedures' },
  { id: 'other', title: 'Other', icon: '📋', description: 'Custom case type' },
];

const mockDealrooms = [
  { id: 1, title: 'Implant Case - John Doe', status: 'Active', patient: 'John Doe', stage: 'Treatment Planning', participants: 4, lastUpdate: '2024-07-20', caseType: 'Dental Implant' },
  { id: 2, title: 'Full Arch Restoration - Jane Smith', status: 'Completed', patient: 'Jane Smith', stage: 'Post-Op', participants: 5, lastUpdate: '2024-07-18', caseType: 'Full Arch Implant Therapy' },
  { id: 3, title: 'Wisdom Teeth Extraction - Mike Johnson', status: 'Archived', patient: 'Mike Johnson', stage: 'Closed', participants: 3, lastUpdate: '2024-06-10', caseType: 'Wisdom Teeth' },
  { id: 4, title: 'Jaw Surgery - Emily White', status: 'Active', patient: 'Emily White', stage: 'Pre-Op Assessment', participants: 6, lastUpdate: '2024-07-21', caseType: 'Jaw Surgery' },
];

const DealroomsPortal = () => {
  const [viewMode, setViewMode] = useState('grid');
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('All');
  const [isCreating, setIsCreating] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [dealroomTitle, setDealroomTitle] = useState('');
  const [caseDescription, setCaseDescription] = useState('');

  const navigate = useNavigate();

  const handleTemplateSelect = (template) => {
    setSelectedTemplate(template);
    setDealroomTitle(template.title);
    setCaseDescription(template.description);
  };

  const clearTemplateSelection = () => {
    setSelectedTemplate(null);
    setDealroomTitle('');
    setCaseDescription('');
  };

  const handleCreateDealroom = () => {
    const newId = mockDealrooms.length + 1;
    console.log({
      title: dealroomTitle,
      description: caseDescription,
      template: selectedTemplate?.id,
    });
    setIsCreating(false);
    clearTemplateSelection();
    navigate(`/dealrooms/${newId}`);
  };
  
  const filteredDealrooms = useMemo(() => {
    return mockDealrooms.filter(dealroom => {
      const matchesSearch = dealroom.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                            dealroom.patient.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesStatus = statusFilter === 'All' || dealroom.status === statusFilter;
      return matchesSearch && matchesStatus;
    });
  }, [searchTerm, statusFilter]);

  const cardVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 },
  };

  return (
    <div className="bg-gray-50 min-h-screen text-gray-800">
      <div className="p-4 md:p-8">
        <header className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Secure Dealrooms</h1>
            <p className="text-gray-600 mt-1">Collaborate on treatment cases with specialists and patients.</p>
          </div>
          <motion.button 
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => setIsCreating(true)}
            className="mt-4 md:mt-0 flex items-center gap-2 bg-brand-primary text-white font-semibold py-2 px-4 rounded-lg shadow-sm hover:bg-brand-primary/90 transition-colors"
          >
            <SafeIcon icon={FiPlus} className="w-5 h-5" />
            New Dealroom
          </motion.button>
        </header>

        <div className="mb-6 p-4 bg-white rounded-lg shadow-sm">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-center">
            <div className="relative md:col-span-1">
              <SafeIcon icon={FiSearch} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Search by title or patient..."
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-primary focus:border-transparent transition"
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
              />
            </div>
            <div className="flex items-center gap-4 md:col-span-2 justify-start md:justify-end">
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium text-gray-700">Status:</span>
                <div className="relative">
                  <select 
                    className="appearance-none bg-gray-100 border border-gray-300 rounded-lg py-2 pl-4 pr-8 text-sm font-medium hover:bg-gray-200 cursor-pointer focus:outline-none focus:ring-2 focus:ring-brand-primary"
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                  >
                    <option>All</option>
                    <option>Active</option>
                    <option>Completed</option>
                    <option>Archived</option>
                  </select>
                  <SafeIcon icon={FiChevronDown} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 pointer-events-none" />
                </div>
              </div>
              <div className="flex items-center gap-1 p-1 bg-gray-100 rounded-lg">
                <button onClick={() => setViewMode('grid')} className={`p-2 rounded-md ${viewMode === 'grid' ? 'bg-white text-brand-primary shadow-sm' : 'text-gray-500 hover:bg-gray-200'}`}>
                  <SafeIcon icon={FiGrid} />
                </button>
                <button onClick={() => setViewMode('list')} className={`p-2 rounded-md ${viewMode === 'list' ? 'bg-white text-brand-primary shadow-sm' : 'text-gray-500 hover:bg-gray-200'}`}>
                  <SafeIcon icon={FiList} />
                </button>
              </div>
            </div>
          </div>
        </div>

        <AnimatePresence>
          {isCreating && (
            <motion.div 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/50 z-40 flex items-center justify-center p-4"
              onClick={() => setIsCreating(false)}
            >
              <motion.div 
                initial={{ y: -50, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                exit={{ y: -50, opacity: 0 }}
                className="bg-white rounded-xl shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col"
                onClick={e => e.stopPropagation()}
              >
                <div className="p-6 border-b flex justify-between items-center">
                  <h2 className="text-2xl font-bold">Create New Dealroom</h2>
                  <button onClick={() => setIsCreating(false)} className="text-gray-500 hover:text-gray-800">
                    <SafeIcon icon={FiX} className="w-6 h-6" />
                  </button>
                </div>
                
                <div className="p-6 overflow-y-auto">
                  <div className="mb-6">
                    <label className="text-sm font-semibold text-gray-600 mb-2 block">Start with a template (optional)</label>
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                      {templates.map(template => (
                        <motion.div
                          key={template.id}
                          onClick={() => handleTemplateSelect(template)}
                          className={`p-4 border-2 rounded-lg cursor-pointer transition-all relative ${selectedTemplate?.id === template.id ? 'border-brand-primary bg-brand-light' : 'border-gray-200 hover:border-gray-400'}`}
                          whileHover={{ scale: 1.05, y: -5 }}
                        >
                          {selectedTemplate?.id === template.id && (
                            <div className="absolute -top-3 -right-3 bg-brand-primary text-white rounded-full p-1">
                              <SafeIcon icon={FiCheckCircle} className="w-4 h-4" />
                            </div>
                          )}
                          <div className="text-3xl mb-2">{template.icon}</div>
                          <h4 className="font-bold text-gray-800">{template.title}</h4>
                          <p className="text-xs text-gray-500 mt-1 h-8">{template.description}</p>
                        </motion.div>
                      ))}
                    </div>
                    {selectedTemplate && (
                       <button onClick={clearTemplateSelection} className="mt-4 text-sm text-brand-primary hover:underline">Clear selection</button>
                    )}
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                        <label htmlFor="dealroomTitle" className="text-sm font-semibold text-gray-600 mb-2 block">Dealroom Title</label>
                        <input 
                          type="text"
                          id="dealroomTitle"
                          value={dealroomTitle}
                          onChange={(e) => setDealroomTitle(e.target.value)}
                          placeholder="e.g., Implant Case - John Doe"
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-primary focus:border-transparent transition"
                        />
                    </div>
                    <div>
                        <label htmlFor="patientName" className="text-sm font-semibold text-gray-600 mb-2 block">Patient Name</label>
                        <input 
                          type="text"
                          id="patientName"
                          placeholder="e.g., John Doe"
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-primary focus:border-transparent transition"
                        />
                    </div>
                    <div className="md:col-span-2">
                         <label htmlFor="caseDescription" className="text-sm font-semibold text-gray-600 mb-2 block">Case Description</label>
                         <textarea 
                           id="caseDescription"
                           rows="4"
                           value={caseDescription}
                           onChange={(e) => setCaseDescription(e.target.value)}
                           placeholder="Describe the case details..."
                           className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-primary focus:border-transparent transition"
                         />
                    </div>
                  </div>
                </div>

                <div className="p-6 border-t flex justify-end gap-4 bg-gray-50">
                   <button onClick={() => setIsCreating(false)} className="py-2 px-6 bg-gray-200 text-gray-700 font-semibold rounded-lg hover:bg-gray-300 transition-colors">Cancel</button>
                   <button onClick={handleCreateDealroom} className="py-2 px-6 bg-brand-primary text-white font-semibold rounded-lg hover:bg-brand-primary/90 transition-colors shadow-sm">Create Dealroom</button>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        <AnimatePresence>
          <motion.div
            key={viewMode}
            className={viewMode === 'grid' ? 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6' : 'space-y-4'}
          >
            {filteredDealrooms.map((dealroom, index) => (
              <motion.div
                key={dealroom.id}
                variants={cardVariants}
                initial="hidden"
                animate="visible"
                transition={{ delay: index * 0.05 }}
                layout
                onClick={() => navigate(`/dealrooms/${dealroom.id}`)}
                className="cursor-pointer group"
              >
                {viewMode === 'grid' ? (
                  <div className="bg-white rounded-lg shadow-sm hover:shadow-xl transition-shadow duration-300 overflow-hidden h-full flex flex-col">
                    <div className="p-5">
                      <div className="flex justify-between items-start">
                        <span className="text-xs font-semibold bg-blue-100 text-blue-800 px-2 py-1 rounded-full">{dealroom.caseType}</span>
                        <span className={`text-xs font-bold px-2 py-1 rounded-full ${
                          dealroom.status === 'Active' ? 'bg-green-100 text-green-800' :
                          dealroom.status === 'Completed' ? 'bg-indigo-100 text-indigo-800' :
                          'bg-gray-100 text-gray-800'
                        }`}>{dealroom.status}</span>
                      </div>
                      <h3 className="text-lg font-bold text-gray-900 mt-4 group-hover:text-brand-primary transition-colors">{dealroom.title}</h3>
                      <p className="text-sm text-gray-500 mt-1">Patient: {dealroom.patient}</p>
                    </div>
                    <div className="mt-auto px-5 py-4 bg-gray-50 border-t">
                      <div className="flex justify-between items-center text-sm text-gray-600">
                        <div>
                          <p className="font-semibold">Stage</p>
                          <p>{dealroom.stage}</p>
                        </div>
                        <div className="text-right">
                          <p className="font-semibold">Participants</p>
                          <p>{dealroom.participants} members</p>
                        </div>
                      </div>
                      <div className="text-xs text-gray-400 mt-3">Last updated: {dealroom.lastUpdate}</div>
                    </div>
                  </div>
                ) : (
                  <div className="bg-white rounded-lg shadow-sm hover:shadow-lg transition-shadow duration-300 p-4 flex items-center space-x-4">
                    <div className="flex-1">
                      <div className="flex items-center justify-between">
                        <h3 className="text-md font-bold text-gray-900 group-hover:text-brand-primary transition-colors">{dealroom.title}</h3>
                        <span className={`text-xs font-bold px-2 py-1 rounded-full ${
                          dealroom.status === 'Active' ? 'bg-green-100 text-green-800' :
                          dealroom.status === 'Completed' ? 'bg-indigo-100 text-indigo-800' :
                          'bg-gray-100 text-gray-800'
                        }`}>{dealroom.status}</span>
                      </div>
                      <div className="mt-2 text-sm text-gray-600 grid grid-cols-2 md:grid-cols-4 gap-4">
                        <p><span className="font-semibold">Patient:</span> {dealroom.patient}</p>
                        <p><span className="font-semibold">Stage:</span> {dealroom.stage}</p>
                        <p><span className="font-semibold">Participants:</span> {dealroom.participants}</p>
                        <p><span className="font-semibold">Updated:</span> {dealroom.lastUpdate}</p>
                      </div>
                    </div>
                  </div>
                )}
              </motion.div>
            ))}
          </motion.div>
        </AnimatePresence>
        {filteredDealrooms.length === 0 && (
          <div className="text-center py-16 text-gray-500">
            <p className="font-semibold">No dealrooms found.</p>
            <p className="text-sm">Try adjusting your search or filters.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default DealroomsPortal;